<template>
  <section class="form-list">
    <div v-show="isLoading || isSaving || isLoadingError" class="loading-box">
      <div class="loading-main">
        <svg v-show="isLoading || isSaving" class="loading-circular" viewBox="25 25 50 50">
          <circle class="loading-path" cx="50" cy="50" r="20" fill="none"/>
        </svg>
        <div v-show="isLoadingError" class="loading-refresh">
          <i class="el-icon-refresh"/>
        </div>
        <div v-show="isLoadingError" class="loading-text">
          数据加载失败，点击重新尝试
        </div>
      </div>
    </div>

    <el-form
      class="form form-max"
      ref="form"
      :model="report"
      label-position="left"
      label-width="80px"
    >
      <div class="report-toolbar">
        <el-form-item v-if="isAdministrator" label="所属门店">
          {{ report.store_name }}
        </el-form-item>
        <el-form-item label="所属日期">
          {{ report.report_date | timeToYmd }}
        </el-form-item>
        <el-form-item label="添加时间">
          {{ report.add_time | timeToYmdHi }}
        </el-form-item>
      </div>

      <summary-bar
        :income="summary.income"
        :expense="summary.expense"
        :refund="summary.refund"
        :reserve-pay="summary.reservePay"
        :reserve="summary.reserve"
      >
        <template v-slot:actions>
          <el-button @click="RouteConfig.dailyReportList.back">返回</el-button>
        </template>
      </summary-bar>

      <div class="report-grid">
      <report-sections
        :report="sectionsReport"
        :list="list"
        :refund-totals="refundTotals"
        :is-administrator="isAdministrator"
        @page="handlePage"
      >
        <template v-slot:income-extra>
          <report-group
            title="其他收入"
            :count="report.other_incomes.length"
            :amount="otherIncomeTotalCents"
            tone="income"
          >
            <div
              v-for="(otherIncome, otherIncomeIndex) in report.other_incomes"
              :key="`other-income-${otherIncomeIndex}`"
              class="other-readonly"
            >
              <span class="other-readonly-name">{{ otherIncome.title }}</span>
              <span class="other-readonly-mode">{{ otherIncome.card_payment_mode_name }}</span>
              <b>{{ otherIncome.actual_payment | formatYuan }}</b>
            </div>
          </report-group>
        </template>

        <template v-slot:after-expense>
          <reserve-panel :report="reserveReport" readonly/>
        </template>
      </report-sections>

      <section class="report-card course-card">
        <div class="report-card__head">
          <h2 class="report-card__title">课程出勤</h2>
        </div>
        <div class="report-card__body">
        <el-table
          :data="courses"
          border
          fit
          highlight-current-row
          show-summary
          :summary-method="getTableSummaries"
        >
          <el-table-column label="课程时间" align="center">
            <template v-slot="{ row }">
              <span>
                {{ row.start_time | timeToHi }}
                {{ ' - '}}
                {{ row.end_time | timeToHi }}
              </span>
            </template>
          </el-table-column>

          <el-table-column label="课程时长" align="center" width="100" prop="duration" type="money">
            <template v-slot="{ row }">
              {{ row.duration | toYuan }}
            </template>
          </el-table-column>
          <el-table-column label="课程类型" align="center" prop="card_type_name"/>
          <el-table-column label="课程名称" align="center">
            <template v-slot="{ row }">
              <router-link class="primary-color" :to="`/courses/${row.id}/manage`" target="_blank">{{ row.course_name }}</router-link>
            </template>
          </el-table-column>
          <el-table-column label="预约人数" align="center" prop="student_count"/>
          <el-table-column label="签到人数" align="center" prop="signed_count"/>
          <el-table-column label="课程老师 / 出勤状态" min-width="360" align="center">
            <template v-slot="{ row }">
              <div
                class="flex-center"
                v-for="(teacher, teacherIndex) in row.teachers"
                :key="`teacher${teacherIndex}`"
              >
                <div class="teacher-state-item">
                  <v-avatar-nickname :value="teacher.avatar" :nickname="teacher.nickname"/>

                  <el-tag
                    v-for="v in findCourseState(teacher.course_state)"
                    :key="`${v.name}`"
                    :type="v.tagType"
                  >
                    {{ v.name }}
                  </el-tag>
                </div>
              </div>
            </template>
          </el-table-column>

          <el-table-column label="代课老师 / 出勤状态" min-width="360" align="center">
            <template v-slot="{ row }">
              <div
                class="flex-center"
                v-for="(teacher, teacherIndex) in row.teachers"
                :key="`teacher${teacherIndex}`"
              >
                <div class="teacher-state-item" v-if="teacher.course_state === courseStates.leave.value">
                  <v-avatar-nickname :value="teacher.sub_avatar" :nickname="teacher.sub_nickname"/>
                  <el-tag
                    v-for="v in findCourseState(teacher.sub_course_state)"
                    :key="`courseStateSub${v.name}`"
                    :type="v.tagType"
                    class="teacher-state-tag"
                  >
                    {{ v.name }}
                  </el-tag>
                </div>
                <template v-else>-</template>
              </div>
            </template>
          </el-table-column>
        </el-table>
        <el-pagination
          v-show="courseCount > 0"
          class="group-pagination"
          :total="courseCount"
        />
        </div>
      </section>
      </div>
    </el-form>
  </section>
</template>

<script>
import RouteConfig from '@/router/config';
import { mapGetters } from 'vuex';
import { courseStates, findCourseState } from '@/enums/courseStates';
import { index as getTeachers } from '@/api/teacher';
import { getDailyCards } from '@/api/card';
import { getIncomes, getCourses, show, getRefunds, getUpdatePayments, getReservationIncomes, getReservationPays, getCardTicketIncomes, getVoucherIncomes, getCardTicketRefunds, getVoucherRefunds } from '@/api/daily-report';
import { getDailyTrialPayments } from "@/api/trial-payment";
import { toYuan, toCent, plus } from "@/filters";
import SummaryBar from './components/SummaryBar.vue';
import ReportSections from './components/ReportSections.vue';
import ReportGroup from './components/ReportGroup.vue';
import ReservePanel from './components/ReservePanel.vue';
import { getTableSummaries } from "@/utils/number";
import { findPaymentModeName } from "@/enums/paymentModes";

export default {
  name: RouteConfig.dailyReportDetail.name,
  components: { SummaryBar, ReportSections, ReportGroup, ReservePanel },
  data: () => ({
    RouteConfig,
    isLoading: false,
    isCardsLoading: false,
    isLoadingError: false,
    isCardsLoadingError: false,
    isSaving: false,
    listPageSize: 10,
    cardListPageSize: 8,
    trailPaymentsPageSize: 8,
    courseCount: 0,
    stores: [],
    newCards: [],
    oldCards: [],
    refunds: [],
    update_incomes: [],
    update_pays: [],
    reservation_incomes: [],
    reservation_pays: [],
    trialPayments: [],
    cardTicketIncomes: [],
    cardTicketRefunds: [],
    cardTicketRefundCount: 0,
    cardTicketRefundTotal: 0,
    voucherIncomes: [],
    voucherRefunds: [],
    voucherRefundCount: 0,
    voucherRefundTotal: 0,
    courses: [],
    teachers: [],
    courseStates,
    pickerOptions: {
      disabledDate: (time) => time.getTime() >= Date.now(),
    },
    report: {
      store_id: '',
      store_name: '',
      report_date: '',
      add_time: '',
      new_income: 0,
      old_income: 0,
      trial_income: 0,
      card_ticket_income: 0,
      voucher_income: 0,
      reservation_income: 0,
      other_income: 0,
      other_incomes: [],
      cash_income: 0,
      card_income: 0,
      you_zan_income: 0,
      dou_yin_income: 0,
      transfer_income: 0,
      applet_income: 0,
      we_chat_income: 0,
      alipay_income: 0,
      meituan_income: 0,
      all_income: 0,
      initial_cash: 0,
      expenditure: 0,
      cash_surplus: 0,
      // 备用金账：期初 + 新增 - 支出 = 期末
      reserve_income: 0,
      reserve_balance: 0,
      // 新增备用金只有「店长转入」一种，固定一行
      reserve_incomes: [{ amount: '', remark: '店长转入' }],
      reserve_pays: [],
      new_count: 0,
      old_count: 0,
      trial_count: 0,
      card_ticket_income_count: 0,
      voucher_income_count: 0,
      reservation_income_count: 0,
      group_course_count: 0,
      refund_count: 0,
      total_attendance: 0,
      average_attendance: 0,
      course_consumption: 0,
      our_store_consumption: 0,
      our_store_other_consumption: 0,
      other_store_consumption: 0,
      profit_consumption: 0,
      refund_pay: 0,
      refund_pay_input_error: 0,
      ticket_refund_pay: 0,
      ticket_refund_count: 0,
      update_income: 0,
      update_income_count: 0,
      update_pay: 0,
      update_pay_count: 0,
      reservation_pay: 0,
      reservation_pay_count: 0,
      fu_zhang_gui_income: 0,
    },
    initial_income: {
      other_income: 0,
      cash_income: 0,
      card_income: 0,
      you_zan_income: 0,
      dou_yin_income: 0,
      transfer_income: 0,
      applet_income: 0,
      we_chat_income: 0,
      alipay_income: 0,
      meituan_income: 0,
      fu_zhang_gui_income: 0,
    },
  }),
  computed: {
    ...mapGetters([
      'isAdministrator',
    ]),
    // 供 ReportSections 渲染的明细数据集合
    list() {
      return {
        newCards: this.newCards,
        oldCards: this.oldCards,
        trialPayments: this.trialPayments,
        cardTicketIncomes: this.cardTicketIncomes,
        voucherIncomes: this.voucherIncomes,
        updateIncomes: this.update_incomes,
        reservationIncomes: this.reservation_incomes,
        refunds: this.refunds,
        cardTicketRefunds: this.cardTicketRefunds,
        voucherRefunds: this.voucherRefunds,
        reservationPays: this.reservation_pays,
        updatePays: this.update_pays,
      };
    },
    refundTotals() {
      return {
        cardTicketCount: this.cardTicketRefundCount,
        cardTicketTotal: this.cardTicketRefundTotal,
        voucherCount: this.voucherRefundCount,
        voucherTotal: this.voucherRefundTotal,
      };
    },
    // 支出合计只算退款类业务支出；备用金支出在备用金账内单独体现
    // 单位提醒：update_pay 被接口转成了元，其余三项是分
    expenseTotal() {
      const { report } = this;
      return plus(
        report.refund_pay || 0,
        report.ticket_refund_pay || 0,
        report.reservation_pay || 0,
        toCent(report.update_pay || 0),
      );
    },
    // ReportSections 统一按「分 + toYuan」渲染，这里把元口径的 update_pay 换回分
    sectionsReport() {
      return {
        ...this.report,
        update_pay: toCent(this.report.update_pay || 0),
      };
    },
    // 结算头按「元」展示；总支出 = 退款类支出 + 备用金支出（详情页两者都是分）
    summary() {
      const refund = this.expenseTotal;
      const reservePay = this.report.expenditure || 0;
      return {
        income: toYuan(this.report.all_income || 0),
        expense: toYuan(plus(refund, reservePay)),
        refund: toYuan(refund),
        reservePay: toYuan(reservePay),
        reserve: toYuan(this.report.reserve_balance || 0),
      };
    },
    // 备用金面板按「元」编辑/展示，单独给一份视图数据
    reserveReport() {
      return {
        initial_cash: toYuan(this.report.initial_cash || 0),
        cash_income: toYuan(this.report.cash_income || 0),
        reserve_incomes: this.report.reserve_incomes,
        reserve_pays: this.report.reserve_pays,
      };
    },
    // 其他收入在详情页按接口实时值展示，金额已是元；分组金额按「分」传递给 ReportGroup
    otherIncomeTotal() {
      return this.report.other_incomes.reduce(
        (total, item) => plus(total, item.actual_payment || 0),
        0,
      );
    },
    otherIncomeTotalCents() {
      return Math.round(this.otherIncomeTotal * 100);
    },
    id() {
      return +this.$route.params.id || 0;
    },
  },
  watch: {
    'report.report_date'() {
      this.handleReportDateAndStoreIdChange();
    },
    'report.store_id'() {
      this.handleReportDateAndStoreIdChange();
    },
  },
  methods: {
    findPaymentModeName,
    findCourseState: (stateValue) => findCourseState(stateValue),
    tableRowClassName({ row }) {
      return row.is_refund ? 'warning-background' : '';
    },
    async getTeachers() {
      const { isError, data } = await getTeachers({
        store_id: this.report.store_id,
        is_brief: 1,
      });

      if (isError) {
        this.isLoadingError = true;
        return;
      }

      this.teachers = data;
    },
    async getDailyCards({ is_new, page }) {
      const { isError, data } = await getDailyCards({
        store_id: this.report.store_id,
        report_date: this.report.report_date,
        is_new,
        page,
        size: this.cardListPageSize,
      });

      if (isError) {
        this.isCardsLoadingError = true;
        return;
      }

      // 详情页只取明细列表；笔数/金额用 show() 返回的提交快照，不能被实时数据覆盖
      if (is_new) {
        this.newCards = data.list;
      } else {
        this.oldCards = data.list;
      }
    },
    async getDailyTrailPayments({ page }) {
      const { isError, data } = await getDailyTrialPayments({
        store_id: this.report.store_id,
        report_date: this.report.report_date,
        page,
        size: this.trailPaymentsPageSize,
      });

      if (isError) {
        this.isCardsLoadingError = true;
        return;
      }

      this.trialPayments = data.list;
    },
    async getCardTicketIncomes(page) {
      const { isError, data } = await getCardTicketIncomes({
        store_id: this.report.store_id,
        report_date: this.report.report_date,
        page,
        size: this.cardListPageSize,
      });

      if (isError) {
        this.isCardsLoadingError = true;
        return;
      }

      this.cardTicketIncomes = data.list || [];
    },
    async getVoucherIncomes(page) {
      const { isError, data } = await getVoucherIncomes({
        store_id: this.report.store_id,
        // 编辑/查看页的 report_date 来自 show 接口，已是秒级时间戳，不能再转
        report_date: this.report.report_date,
        page,
        size: this.cardListPageSize,
      });

      if (isError) {
        this.isListLoadingError = true;
        return;
      }

      this.voucherIncomes = data.list || [];
    },
    async getCardTicketRefunds(page) {
      const { isError, data } = await getCardTicketRefunds({
        store_id: this.report.store_id,
        report_date: this.report.report_date,
        page,
        size: this.cardListPageSize,
      });

      if (isError) {
        this.isListLoadingError = true;
        return;
      }

      this.cardTicketRefunds = data.list || [];
      this.cardTicketRefundCount = data.count || 0;
      this.cardTicketRefundTotal = data.total || 0;
    },
    async getVoucherRefunds(page) {
      const { isError, data } = await getVoucherRefunds({
        store_id: this.report.store_id,
        report_date: this.report.report_date,
        page,
        size: this.cardListPageSize,
      });

      if (isError) {
        this.isListLoadingError = true;
        return;
      }

      this.voucherRefunds = data.list || [];
      this.voucherRefundCount = data.count || 0;
      this.voucherRefundTotal = data.total || 0;
    },
    async getRefunds() {
      const { isError, data } = await getRefunds({
        store_id: this.report.store_id,
        report_date: this.report.report_date,
      });

      if (isError) {
        this.isCardsLoadingError = true;
        return;
      }

      this.refunds = data.list;
    },
    async getUpdatePayments(is_income) {
      const { isError, data } = await getUpdatePayments({
        store_id: this.report.store_id,
        report_date: this.report.report_date,
        is_income,
      });

      if (isError) {
        this.isCardsLoadingError = true;
        return;
      }

      if (is_income) {
        this.update_incomes = data.list || [];
      } else {
        this.update_pays = data.list || [];
      }
    },
    async getReservationIncomes(page) {
      const { isError, data } = await getReservationIncomes({
        store_id: this.report.store_id,
        report_date: this.report.report_date,
        page: page,
        size: this.listPageSize,
      });

      if (isError) {
        this.isCardsLoadingError = true;
        return;
      }

      this.reservation_incomes = data.list || [];
    },
    async getReservationPays(page) {
      const { isError, data } = await getReservationPays({
        store_id: this.report.store_id,
        report_date: this.report.report_date,
        page: page,
        size: this.listPageSize,
      });

      if (isError) {
        this.isCardsLoadingError = true;
        return;
      }

      this.reservation_pays = data.list || [];
    },
    async getIncomes() {
      const { isError, data } = await getIncomes({
        store_id: this.report.store_id,
        report_date: this.report.report_date,
      });

      if (isError) {
        this.isCardsLoadingError = true;
        return;
      }

      if (data.other_incomes) {
        data.other_incomes.forEach(otherIncome => {
          otherIncome.actual_payment = toYuan(otherIncome.actual_payment);
        });
      }

      // 只补「其他收入」明细；收入/支出/笔数一律用 show() 拿到的提交快照。
      // 之前这里整体 Object.assign 了实时数据，异步返回后统计条会被改写成实时值（历史日期通常是 0）。
      this.report.other_incomes = data.other_incomes || [];
    },
    async getDailyCourses() {
      const { isError, data } = await getCourses({
        store_id: this.report.store_id,
        report_date: this.report.report_date,
      });

      if (isError) {
        this.isCardsLoadingError = true;
        return;
      }

      this.courses = data;
    },
    getTableSummaries,
    // 分组内分页：按分类 key 转发到对应接口
    handlePage({ key, page }) {
      const handlers = {
        new: () => this.getDailyCards({ is_new: 1, page }),
        old: () => this.getDailyCards({ is_new: 0, page }),
        trial: () => this.getDailyTrailPayments({ page }),
        cardTicket: () => this.getCardTicketIncomes(page),
        voucher: () => this.getVoucherIncomes(page),
        reservationIncome: () => this.getReservationIncomes(page),
        cardTicketRefund: () => this.getCardTicketRefunds(page),
        voucherRefund: () => this.getVoucherRefunds(page),
        reservationPay: () => this.getReservationPays(page),
      };
      if (handlers[key]) handlers[key]();
    },
    // 把接口返回的备用金流水拆成「新增 / 支出」两组展示行
    normalizeReserveRecords(data) {
      const records = data.reserve_records || [];
      const toRows = type => records
        .filter(item => item.type === type)
        .map(item => ({ amount: toYuan(item.amount), remark: item.remark || '' }));
      const incomes = toRows(1).slice(0, 1);
      if (!incomes.length) incomes.push({ amount: '', remark: '店长转入' });
      const pays = toRows(2);
      // 旧数据只有合计、没有流水：按一条历史合计展示，避免金额凭空消失
      if (!records.length && data.expenditure > 0) {
        pays.push({ amount: toYuan(data.expenditure), remark: '历史数据' });
      }
      return { incomes, pays };
    },
    async handleReportDateAndStoreIdChange() {
      if (this.isCardsLoading || !this.report.report_date) return;
      if (this.isAdministrator && !this.report.store_id) return;

      this.isCardsLoading = true;
      this.isCardsLoadingError = false;

      await Promise.all([
        this.getDailyCards({ is_new: 1 }),
        this.getDailyCards({ is_new: 0 }),
        this.getDailyTrailPayments({}),
        this.getCardTicketIncomes(1),
        this.getVoucherIncomes(1),
        this.getCardTicketRefunds(1),
        this.getVoucherRefunds(1),
        this.getRefunds(),
        this.getIncomes(),
        this.getDailyCourses(),
        this.getTeachers(),
        this.getUpdatePayments(1),
        this.getUpdatePayments(0),
        this.getReservationIncomes(1),
        this.getReservationPays(1),
      ]);

      this.isCardsLoading = false;
    },
    async show() {
      const { isError, data } = await show(this.id);
      if (isError) return;

      const { report } = this;
      const reserve = this.normalizeReserveRecords(data);
      Object.keys(data).forEach(key => {
        if (key in report) report[key] = data[key];
      });

      // 旧数据没有 reserve_balance，按「期初 - 支出」补一个期末（单位：分），避免显示 0
      if (data.reserve_balance === null || data.reserve_balance === undefined) {
        report.reserve_balance = (data.initial_cash || 0) - (data.expenditure || 0);
      }
      report.reserve_incomes = reserve.incomes;
      report.reserve_pays = reserve.pays;

      // show() 已经拿到提交时的快照；后面拉的接口只用来填明细表格，
      // 不再回写 report 上的金额与笔数（see getIncomes / getDailyCards 等）
      await this.handleReportDateAndStoreIdChange();
    },
  },
  beforeMount() {
    this.show();
  },
};
</script>

<style src="./report-card.css"></style>
<style src="./add.css" scoped/>
